import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { selectIsAuthenticated } from 'app/containers/AuthProvider/selectors';
import { useUserGroups } from 'hooks/useUserGroups';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

// Layout et nouveaux composants
import FullScreenLayout from 'app/components/FullScreenLayout';
import StatusPage from 'app/components/StatusPage';
import BigErrorIcon from 'app/components/icons/BigErrorIcon';
import BigLockIcon from 'app/components/icons/BigLockIcon';

import { removeToken } from 'app/containers/AuthProvider/slice';

// Un spinner plus grand et plus joli
const LargeSpinner = () => (
  <div className="text-center">
    <svg
      className="animate-spin h-16 w-16 text-blue-600 mx-auto"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
    <p className="mt-4 text-xl text-gray-700 font-semibold">
      Un instant, nous préparons votre espace...
    </p>
  </div>
);

const PrivateRoute = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { hasGroup, isLoading, error, refetch } = useUserGroups();
  const location = useLocation();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(removeToken());
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isLoading) {
    return (
      <FullScreenLayout>
        <LargeSpinner />
      </FullScreenLayout>
    );
  }

  if (error) {
    const status = (error as FetchBaseQueryError).status;
    const errorMessage =
      status === 'FETCH_ERROR'
        ? 'Veuillez vérifier votre connexion et réessayer. Si le problème persiste, réessayez plus tard.'
        : 'Une erreur inattendue est survenue lors de la vérification de vos accès. Si le problème persiste, réessayez plus tard.';

    return (
      <FullScreenLayout>
        <StatusPage
          icon={<BigErrorIcon />}
          title="Oups... Erreur Réseau"
          message={<p>{errorMessage}</p>}
          actions={
            <button
              onClick={() => refetch()}
              className="px-6 py-3 bg-primary text-white font-bold rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-transform"
            >
              Réessayer
            </button>
          }
        />
      </FullScreenLayout>
    );
  }

  if (!hasGroup('expedinet')) {
    return (
      <FullScreenLayout>
        <StatusPage
          icon={<BigLockIcon />}
          title="Accès Non Autorisé"
          message={
            <>
              <p>
                Votre compte n'a pas les permissions requises pour accéder à
                cette application.
              </p>
              <p className="text-base text-gray-500 mt-2">
                Si vous pensez qu'il s'agit d'une erreur, veuillez contacter
                votre administrateur.
              </p>
            </>
          }
          actions={
            <button
              onClick={handleLogout}
              className="px-6 py-3 bg-gray-700 text-white font-bold rounded-lg shadow-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transform hover:scale-105 transition-transform"
            >
              Se déconnecter
            </button>
          }
        />
      </FullScreenLayout>
    );
  }

  return <Outlet />;
};

export default PrivateRoute;
